import {
  PackRow,
  PackListResponse,
  ModlistHashLookupResponse,
  CFModlistHashLookupResponse,
  SnapshotListResponse,
  PackTimeseriesResponse,
  ModlistListResponse,
  ModlistDiffResponse,
  ExternalIdListResponse,
  CurseForgeFileListResponse,
  ChangelogListResponse,
  RecentUpdatesResponse,
  DownloadGrowthResponse,
  EmergingResponse,
  ReadyResponse,
  PackEnrichmentResponse,
  ListPacksParams,
  TimeseriesParams,
  ModlistDiffParams,
  RecentUpdatesParams,
  DownloadGrowthParams,
  EmergingParams,
  ChangelogParams,
  PackSearchResponse,
  SearchPacksParams,
  ApogeeSearchDisabledError,
  CurseForgeFeaturedHistoryResponse,
  CurseForgeFileMetricPointsResponse,
  FileMetricPointsParams,
} from '../types/apogee';

const API_BASE_URL = 'https://apogee.playcdu.co';

const SEARCH_PACKS_TIMEOUT_MS = 15_000;

class ApogeeApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  private async requestText(endpoint: string, options?: RequestInit): Promise<string> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.text();
  }

  private buildQueryString(params: Record<string, string | number | boolean | undefined | null>): string {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        qs.append(key, String(value));
      }
    }
    const str = qs.toString();
    return str ? `?${str}` : '';
  }

  // ── Health Endpoints ────────────────────────────────────────────────────

  /** GET /healthz — Liveness probe */
  async healthCheck(): Promise<string> {
    return this.requestText('/healthz');
  }

  /** GET /readyz — Readiness (PostgreSQL ping) */
  async readyCheck(): Promise<ReadyResponse> {
    return this.request<ReadyResponse>('/readyz');
  }

  // ── Pack Listing & Lookup ───────────────────────────────────────────────

  /** GET /v1/packs — List modpacks (paginated, sortable) */
  async listPacks(params: ListPacksParams = {}, signal?: AbortSignal): Promise<PackListResponse> {
    const qs = this.buildQueryString({
      limit: params.limit,
      offset: params.offset,
      sort: params.sort,
    });
    return this.request<PackListResponse>(`/v1/packs${qs}`, { signal });
  }

  /**
   * GET /v1/search/packs — Full-text search (Elasticsearch via Apogee).
   * 404 means search is not configured; use `ApogeeSearchDisabledError` or fall back to `listPacks`.
   */
  async searchPacks(params: SearchPacksParams, signal?: AbortSignal): Promise<PackSearchResponse> {
    const qs = this.buildQueryString({
      q: params.q.trim(),
      limit: params.limit,
      offset: params.offset,
    });
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SEARCH_PACKS_TIMEOUT_MS);
    const onExternalAbort = () => controller.abort();
    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener('abort', onExternalAbort);
    }
    try {
      const response = await fetch(`${API_BASE_URL}/v1/search/packs${qs}`, {
        signal: controller.signal,
      });
      if (response.status === 404) {
        let msg = 'Search is not configured on this deployment.';
        try {
          const body = (await response.json()) as { error?: { message?: string } };
          if (body?.error?.message) msg = body.error.message;
        } catch {
          /* ignore */
        }
        throw new ApogeeSearchDisabledError(msg);
      }
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
      }
      return response.json() as Promise<PackSearchResponse>;
    } finally {
      clearTimeout(timeoutId);
      if (signal) signal.removeEventListener('abort', onExternalAbort);
    }
  }

  /** GET /v1/packs/{mpi_id} — Get one pack + latest metrics */
  async getPackByMpiId(mpiId: number): Promise<PackRow> {
    return this.request<PackRow>(`/v1/packs/${encodeURIComponent(mpiId)}`);
  }

  /** GET /v1/packs/id/{apogee_id} — Get one pack by internal Apogee ID */
  async getPackByApogeeId(apogeeId: number): Promise<PackRow> {
    return this.request<PackRow>(`/v1/packs/id/${encodeURIComponent(apogeeId)}`);
  }

  /** GET /v1/packs/cf/{cf_mod_id} — Get one pack by CurseForge project id (CF-primary accessor) */
  async getPackByCfModId(cfModId: number): Promise<PackRow> {
    return this.request<PackRow>(`/v1/packs/cf/${encodeURIComponent(cfModId)}`);
  }

  /** GET /v1/packs/{mpi_id}/enrichment — Modrinth & CurseForge enrichment snapshots */
  async getPackEnrichment(mpiId: number): Promise<PackEnrichmentResponse> {
    return this.request<PackEnrichmentResponse>(`/v1/packs/${encodeURIComponent(mpiId)}/enrichment`);
  }

  /** GET /v1/packs/id/{apogee_id}/enrichment — Same enrichment by Apogee ID */
  async getPackEnrichmentByApogeeId(apogeeId: number): Promise<PackEnrichmentResponse> {
    return this.request<PackEnrichmentResponse>(`/v1/packs/id/${encodeURIComponent(apogeeId)}/enrichment`);
  }

  /** GET /v1/packs/by-modlist-hash/{hash} — Find packs by MPI modlist SHA-256 hash */
  async findPacksByModlistHash(hash: string, sort?: string): Promise<ModlistHashLookupResponse> {
    const qs = this.buildQueryString({ sort });
    return this.request<ModlistHashLookupResponse>(`/v1/packs/by-modlist-hash/${encodeURIComponent(hash)}${qs}`);
  }

  /** GET /v1/packs/by-cf-modlist-hash/{hash} — Find packs by CurseForge modlist SHA-256 hash */
  async findPacksByCFModlistHash(hash: string, sort?: string): Promise<CFModlistHashLookupResponse> {
    const qs = this.buildQueryString({ sort });
    return this.request<CFModlistHashLookupResponse>(`/v1/packs/by-cf-modlist-hash/${encodeURIComponent(hash)}${qs}`);
  }

  // ── Per-Pack Detail Endpoints ───────────────────────────────────────────

  /** GET /v1/packs/{mpi_id}/snapshots — Time series of snapshots */
  async getPackSnapshots(mpiId: number, limit?: number): Promise<SnapshotListResponse> {
    const qs = this.buildQueryString({ limit });
    return this.request<SnapshotListResponse>(`/v1/packs/${encodeURIComponent(mpiId)}/snapshots${qs}`);
  }

  /** GET /v1/packs/{mpi_id}/modlists — Historical mod lists */
  async getPackModlists(mpiId: number, limit?: number): Promise<ModlistListResponse> {
    const qs = this.buildQueryString({ limit });
    return this.request<ModlistListResponse>(`/v1/packs/${encodeURIComponent(mpiId)}/modlists${qs}`);
  }

  /** GET /v1/packs/{mpi_id}/timeseries — Download & rank time series (TimescaleDB) */
  async getPackTimeseries(mpiId: number, params: TimeseriesParams): Promise<PackTimeseriesResponse> {
    const qs = this.buildQueryString({
      from: params.from,
      to: params.to,
      bucket: params.bucket,
      limit: params.limit,
    });
    return this.request<PackTimeseriesResponse>(`/v1/packs/${encodeURIComponent(mpiId)}/timeseries${qs}`);
  }

  /** GET /v1/packs/id/{apogee_id}/timeseries — Same timeseries by internal Apogee ID */
  async getPackTimeseriesByApogeeId(apogeeId: number, params: TimeseriesParams): Promise<PackTimeseriesResponse> {
    const qs = this.buildQueryString({
      from: params.from,
      to: params.to,
      bucket: params.bucket,
      limit: params.limit,
    });
    return this.request<PackTimeseriesResponse>(`/v1/packs/id/${encodeURIComponent(apogeeId)}/timeseries${qs}`);
  }

  /** GET /v1/packs/{mpi_id}/modlist-diff — Added/removed mod IDs between snapshots */
  async getPackModlistDiff(mpiId: number, params: ModlistDiffParams): Promise<ModlistDiffResponse> {
    const qs = this.buildQueryString({
      from_observed_at: params.from_observed_at,
      to_observed_at: params.to_observed_at,
      from_snapshot_id: params.from_snapshot_id,
      to_snapshot_id: params.to_snapshot_id,
    });
    return this.request<ModlistDiffResponse>(`/v1/packs/${encodeURIComponent(mpiId)}/modlist-diff${qs}`);
  }

  /** GET /v1/packs/{mpi_id}/external-ids — Mapped CurseForge / Modrinth IDs */
  async getPackExternalIds(mpiId: number): Promise<ExternalIdListResponse> {
    return this.request<ExternalIdListResponse>(`/v1/packs/${encodeURIComponent(mpiId)}/external-ids`);
  }

  /** GET /v1/packs/{mpi_id}/curseforge/files — Cached CurseForge file rows */
  async getPackCurseForgeFiles(mpiId: number, limit?: number): Promise<CurseForgeFileListResponse> {
    const qs = this.buildQueryString({ limit });
    return this.request<CurseForgeFileListResponse>(`/v1/packs/${encodeURIComponent(mpiId)}/curseforge/files${qs}`);
  }

  /**
   * GET /v1/packs/id/{apogee_id}/curseforge/files — the same cached CurseForge file rows, by internal
   * Apogee ID. Partner packs are linked by Apogee ID, so this is how a report is matched to a file.
   */
  async getPackCurseForgeFilesByApogeeId(apogeeId: number, limit?: number): Promise<CurseForgeFileListResponse> {
    const qs = this.buildQueryString({ limit });
    return this.request<CurseForgeFileListResponse>(`/v1/packs/id/${encodeURIComponent(apogeeId)}/curseforge/files${qs}`);
  }

  /** GET /v1/packs/{mpi_id}/curseforge/changelogs — Changelog excerpts (HTML) */
  async getPackCurseForgeChangelogs(mpiId: number, params: ChangelogParams = {}): Promise<ChangelogListResponse> {
    const qs = this.buildQueryString({
      limit: params.limit,
      full: params.full ? 1 : undefined,
    });
    return this.request<ChangelogListResponse>(`/v1/packs/${encodeURIComponent(mpiId)}/curseforge/changelogs${qs}`);
  }

  /** GET /v1/packs/{mpi_id}/curseforge/featured-history — Featured carousel span history */
  async getPackCurseForgeFeaturedHistory(mpiId: number): Promise<CurseForgeFeaturedHistoryResponse> {
    return this.request<CurseForgeFeaturedHistoryResponse>(
      `/v1/packs/${encodeURIComponent(mpiId)}/curseforge/featured-history`,
    );
  }

  /** GET /v1/packs/id/{apogee_id}/curseforge/featured-history */
  async getPackCurseForgeFeaturedHistoryByApogeeId(apogeeId: number): Promise<CurseForgeFeaturedHistoryResponse> {
    return this.request<CurseForgeFeaturedHistoryResponse>(
      `/v1/packs/id/${encodeURIComponent(apogeeId)}/curseforge/featured-history`,
    );
  }

  /** GET /v1/packs/{mpi_id}/curseforge/file-metric-points — Per-file download counter observations (newest first) */
  async getPackCurseForgeFileMetricPoints(
    mpiId: number,
    params: FileMetricPointsParams = {},
  ): Promise<CurseForgeFileMetricPointsResponse> {
    const qs = this.buildQueryString({ limit: params.limit });
    return this.request<CurseForgeFileMetricPointsResponse>(
      `/v1/packs/${encodeURIComponent(mpiId)}/curseforge/file-metric-points${qs}`,
    );
  }

  /** GET /v1/packs/id/{apogee_id}/curseforge/file-metric-points */
  async getPackCurseForgeFileMetricPointsByApogeeId(
    apogeeId: number,
    params: FileMetricPointsParams = {},
  ): Promise<CurseForgeFileMetricPointsResponse> {
    const qs = this.buildQueryString({ limit: params.limit });
    return this.request<CurseForgeFileMetricPointsResponse>(
      `/v1/packs/id/${encodeURIComponent(apogeeId)}/curseforge/file-metric-points${qs}`,
    );
  }

  // ── Insights Endpoints ──────────────────────────────────────────────────

  /** GET /v1/insights/recent-updates — Packs updated recently */
  async getRecentUpdates(params: RecentUpdatesParams = {}): Promise<RecentUpdatesResponse> {
    const qs = this.buildQueryString({
      since_hours: params.since_hours,
      limit: params.limit,
      sort: params.sort,
    });
    return this.request<RecentUpdatesResponse>(`/v1/insights/recent-updates${qs}`);
  }

  /** GET /v1/insights/download-growth — Top packs by download delta */
  async getDownloadGrowth(params: DownloadGrowthParams = {}): Promise<DownloadGrowthResponse> {
    const qs = this.buildQueryString({
      window_days: params.window_days,
      limit: params.limit,
      sort: params.sort,
    });
    return this.request<DownloadGrowthResponse>(`/v1/insights/download-growth${qs}`);
  }

  /** GET /v1/insights/emerging — Packs first-seen recently with large growth */
  async getEmerging(params: EmergingParams = {}): Promise<EmergingResponse> {
    const qs = this.buildQueryString({
      first_seen_within_days: params.first_seen_within_days,
      min_delta_downloads: params.min_delta_downloads,
      limit: params.limit,
      sort: params.sort,
    });
    return this.request<EmergingResponse>(`/v1/insights/emerging${qs}`);
  }
}

export const apogeeApiService = new ApogeeApiService();
